/*Bearer ${token}`
 * vatdashboard.js — Phase 5 VAT Dashboard
 *
 * Shows all clients with their VAT status, AI confidence, and quick actions.
 * Provides the "AI-first" workflow overview as described in Phase 5.
 */

import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useAuthz } from "context/AuthzContext";
import { listClientProfiles, apiRequest } from "services/tenantUserAdminService";
import { useHistory } from "react-router-dom";
import { TC } from "../tokens/text";

const CARD_BG     = "linear-gradient(127.09deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)";
const CARD_BORDER = "1px solid rgba(255,255,255,0.08)";

const STATUS_META = {
  draft:     { label: "Draft",     color: "#F7B731" },
  approved:  { label: "Approved",  color: "#01B574" },
  submitted: { label: "Submitted", color: "#4E72CE" },
  filed:     { label: "Filed",     color: "#A29BFE" },
  none:      { label: "Not Started", color: TC.secondary },
};

function fmtGBP(v) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);
}

function AiConfidenceBadge({ value }) {
  if (value == null) return <VuiTypography sx={{ color: TC.secondary, fontSize: 11 }}>—</VuiTypography>;
  const pct = Math.round(value * 100);
  const color = pct >= 90 ? "#01B574" : pct >= 70 ? "#F7B731" : "#f44336";
  return (
    <VuiBox>
      <VuiBox display="flex" justifyContent="space-between" mb={0.3}>
        <VuiTypography sx={{ color: TC.secondary, fontSize: 10 }}>AI Confidence</VuiTypography>
        <VuiTypography sx={{ color, fontSize: 10, fontWeight: 700 }}>{pct}%</VuiTypography>
      </VuiBox>
      <LinearProgress variant="determinate" value={pct}
        aria-label={`AI categorisation confidence: ${pct}%`}
        sx={{ height: 4, borderRadius: 2, background: "#f4f8ff", "& .MuiLinearProgress-bar": { background: color } }} />
    </VuiBox>
  );
}

function PeriodSelector({ value, onChange }) {
  const now = new Date();
  const options = Array.from({ length: 8 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return d.toISOString().slice(0, 7);
  });
  return (
    <Select aria-label="Select VAT period" value={value} onChange={e => onChange(e.target.value)} size="small"
      sx={{ color: TC.primary, background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: 2, fontSize: 13, "& .MuiSvgIcon-root": { color: TC.primary } }}>
      {options.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
    </Select>
  );
}

function ClientVATCard({ profile, period, tenantId, onNavigate }) {
  const [draft, setDraft]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [generating, setGenerating] = useState(false);

  const clientId   = profile.id;
  const clientName = profile.legalName || profile.tradingName || clientId;

  useEffect(() => {
    if (!tenantId || !clientId) return;
    setLoading(true);
    apiRequest(`/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/vat-drafts/${period}`)
      .then(d => setDraft(d))
      .catch(e => {
        if (e.message?.includes("404") || e.message?.toLowerCase().includes("not found")) setDraft(null);
      })
      .finally(() => setLoading(false));
  }, [tenantId, clientId, period]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const d = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/vat-drafts/${period}/generate`,
        { method: "POST" }
      );
      setDraft(d);
    } catch (_) {} finally { setGenerating(false); }
  };

  const handleRunAI = async () => {
    setAiRunning(true);
    try {
      const [review] = await Promise.all([
        apiRequest(`/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/vat-drafts/${period}/ai/review`, { method: "POST" }),
        apiRequest(`/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/vat-drafts/${period}/ai/filing-check`, { method: "POST" }),
      ]);
      setDraft(prev => prev ? { ...prev, aiReview: review } : prev);
    } catch (_) {} finally { setAiRunning(false); }
  };

  const status   = draft?.status || "none";
  const meta     = STATUS_META[status] || STATUS_META.none;
  const boxes    = draft?.boxes || {};
  const aiConf   = draft?.aiCategorisation?.averageConfidence ?? null;
  const aiReview = draft?.aiReview;
  const riskColor = aiReview?.riskLevel === "low" ? "#01B574" : aiReview?.riskLevel === "high" ? "#f44336" : aiReview?.riskLevel === "medium" ? "#F7B731" : null;

  return (
    <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px", height: "100%" }}>
      <VuiBox p={2.5}>
        {/* Client header */}
        <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <VuiBox>
            <VuiTypography sx={{ color: TC.primary, fontSize: 14, fontWeight: 700 }}>{clientName}</VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 11 }}>{period}</VuiTypography>
          </VuiBox>
          <Chip label={meta.label} size="small"
            sx={{ background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}44`, fontSize: 10, fontWeight: 700, height: 20 }} />
        </VuiBox>

        {loading ? (
          <VuiTypography sx={{ color: TC.secondary, fontSize: 12 }}>Loading…</VuiTypography>
        ) : draft ? (
          <>
            {/* Key figures */}
            <VuiBox display="flex" justifyContent="space-between" mb={1}>
              <VuiTypography sx={{ color: TC.secondary, fontSize: 12 }}>Net VAT (Box 5)</VuiTypography>
              <VuiTypography sx={{ color: (boxes.box5 || 0) < 0 ? "#01B574" : "#F7B731", fontSize: 12, fontWeight: 700 }}>
                {fmtGBP(boxes.box5)}
              </VuiTypography>
            </VuiBox>
            <VuiBox display="flex" justifyContent="space-between" mb={1.5}>
              <VuiTypography sx={{ color: TC.secondary, fontSize: 12 }}>Sales (Box 6)</VuiTypography>
              <VuiTypography sx={{ color: TC.midtone, fontSize: 12, fontWeight: 600 }}>
                {fmtGBP(boxes.box6)}
              </VuiTypography>
            </VuiBox>

            {/* AI Confidence */}
            <AiConfidenceBadge value={aiConf} />

            {/* AI risk */}
            {riskColor && (
              <VuiBox mt={1} display="flex" alignItems="center" gap={1} role="status" aria-label={`AI risk level: ${aiReview.riskLevel}`}>
                <span style={{ background: `${riskColor}15`, color: riskColor, padding: "2px 7px", borderRadius: 5, fontSize: 10, fontWeight: 700, textTransform: "capitalize" }}>
                  AI Risk: {aiReview.riskLevel}
                </span>
                {(aiReview.anomalies || []).length > 0 && (
                  <Tooltip title={aiReview.anomalies.map(a => a.description || a).join("\n")} placement="top">
                    <span role="note" tabIndex={0} style={{ color: "#F7B731", fontSize: 10, cursor: "pointer" }}
                      aria-label={`${aiReview.anomalies.length} anomal${aiReview.anomalies.length === 1 ? "y" : "ies"} detected`}>
                      ⚠ {aiReview.anomalies.length} anomal{aiReview.anomalies.length === 1 ? "y" : "ies"}
                    </span>
                  </Tooltip>
                )}
              </VuiBox>
            )}

            {/* Actions */}
            <VuiBox mt={2} display="flex" gap={1} flexWrap="wrap">
              <VuiButton size="small" color="warning" variant="outlined"
                onClick={handleRunAI} disabled={aiRunning || status === "submitted" || status === "filed"}
                sx={{ fontSize: 10, py: 0.3, px: 1 }}
                aria-label={`Run AI review for ${clientName}`}>
                {aiRunning ? "Running AI…" : "🤖 AI Review"}
              </VuiButton>
              <VuiButton size="small" color="info" variant="contained"
                onClick={() => onNavigate(clientId)} sx={{ fontSize: 10, py: 0.3, px: 1 }}
                aria-label={`Open VAT return for ${clientName}`}>
                Open Return →
              </VuiButton>
            </VuiBox>
          </>
        ) : (
          <VuiBox>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 12, mb: 1.5 }}>No draft for this period.</VuiTypography>
            <VuiButton size="small" color="info" variant="outlined"
              onClick={handleGenerate} disabled={generating} sx={{ fontSize: 10, py: 0.3, px: 1 }}
              aria-label={`Generate VAT draft for ${clientName}`}>
              {generating ? "Generating…" : "Generate Draft"}
            </VuiButton>
          </VuiBox>
        )}
      </VuiBox>
    </Card>
  );
}

export default function VATDashboard() {
  const { tenantId }    = useAuthz();
  const history         = useHistory();
  const [profiles, setProfiles] = useState([]);
  const [period, setPeriod]     = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    listClientProfiles({ tenantId, status: "active" })
      .then(d => setProfiles(d.clientProfiles || []))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const navigateToReturn = (clientId) => {
    history.push(`/vat-return?client=${clientId}&period=${period}`);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>📊 VAT Dashboard</VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
              AI-powered VAT health overview for all clients — {period}
            </VuiTypography>
          </VuiBox>
          <PeriodSelector value={period} onChange={setPeriod} />
        </VuiBox>

        {/* Summary stats */}
        {profiles.length > 0 && (
          <VuiBox mb={3} display="flex" gap={2} flexWrap="wrap">
            {[
              { label: "Active Clients", value: profiles.length, color: "#4E72CE" },
              { label: "Period", value: period, color: TC.midtone },
            ].map(({ label, value, color }) => (
              <VuiBox key={label} sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "12px", p: "14px 20px", minWidth: 140 }}>
                <VuiTypography sx={{ color: TC.secondary, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</VuiTypography>
                <VuiTypography sx={{ color, fontSize: 20, fontWeight: 700, mt: 0.3 }}>{value}</VuiTypography>
              </VuiBox>
            ))}
          </VuiBox>
        )}

        {loading ? (
          <LoadingSpinner label="Loading clients…" />
        ) : profiles.length === 0 ? (
          <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
            <VuiBox p={4} textAlign="center">
              <VuiTypography sx={{ color: TC.secondary, fontSize: 14 }}>No active clients found.</VuiTypography>
            </VuiBox>
          </Card>
        ) : (
          <Grid container spacing={2.5}>
            {profiles.map(profile => (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={profile.id}>
                <ClientVATCard
                  profile={profile}
                  period={period}
                  tenantId={tenantId}
                  onNavigate={navigateToReturn}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}


